import { useContext } from "react";
import dayjs from "dayjs";
import { useParams, useLocation } from "react-router-dom";
import { Box, Typography, Grid, CircularProgress, Paper, Chip, Divider } from "@mui/material";
import LocalShippingTwoToneIcon from "@mui/icons-material/LocalShippingTwoTone";
import RoomServiceTwoToneIcon from "@mui/icons-material/RoomServiceTwoTone";
import Layout from "../components/Layout/Layout";
import WorkshopLineClock from "../components/WorkshopLineClock/WorkshopLineClock";
import { useFetchWorkshopOrderByIdQuery } from "../api";
import { UserDataContext } from "../app/App";
import { getPriorityChip } from "../components/WorkshopOrdersList/WorkshopOrdersList";
import ErrorPage from "./ErrorPage";
import { green } from "@mui/material/colors";

function WorkshopOrder() {
    const { equipmentId, workshopOrderId } = useParams();

    const location = useLocation();

    const workshopOrderProp = location.state?.workshopOrder;
    const { user } = useContext(UserDataContext);

    const { data: workshopOrderById, isLoading: isLoadingWorkshopOrder, isError: isErrorWorkshopOrder } = useFetchWorkshopOrderByIdQuery(workshopOrderId, !workshopOrderProp);

    if (isErrorWorkshopOrder) {
        return <ErrorPage />;
    }

    if (isLoadingWorkshopOrder) {
        return (
            <Layout title={`Workshop Order`} subtitle={user.name}>
                <Box display="flex" justifyContent="center">
                    <CircularProgress />
                </Box>
            </Layout>
        );
    }

    const workshopOrder = workshopOrderProp || workshopOrderById;
    const { priority = "N/A", order = {} } = workshopOrder;

    const {
        description = null,
        name: orderNumber = "N/A",
        createdOn: orderCreatedOn = "N/A",
        reporter: { name: reporterName = "N/A" } = {},
        vehicle: { number: equipmentNumber = "N/A", vehicleType: { type = null } = {} } = {},
    } = order;
    const formatDaysElapsed = (date) => {
        const daysAgo = dayjs().diff(dayjs(date), "day");
        return daysAgo === 0 ? "Today" : daysAgo === 1 ? "1d" : `${daysAgo}d`;
    };

    return (
        <Layout title={`Workshop Order`} subtitle={user.name} color={green[400]}>
            <Grid container direction="column" spacing={2}>
                {/* Workshop order details */}
                <Grid item container direction="column" spacing={1}>
                    <Grid item>
                        <Box component={Paper} variant="outlined" display="flex" alignItems="center" elevation={0} p={2}>
                            <Box mr={1}>
                                <LocalShippingTwoToneIcon />
                            </Box>
                            <Box>
                                <Typography style={{ fontWeight: "bold" }}>{`${type} - ${equipmentNumber}`}</Typography>
                            </Box>
                        </Box>
                    </Grid>

                    <Grid item>
                        <Box component={Paper} variant="outlined" display="flex" flexDirection="column" elevation={0} p={2}>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Box display="flex">
                                    <Box mr={1}>
                                        <RoomServiceTwoToneIcon />
                                    </Box>
                                    <Box>
                                        <Typography style={{ fontWeight: "bold" }}>{`#${orderNumber}`}</Typography>
                                    </Box>
                                </Box>
                                <Box>{getPriorityChip(priority)}</Box>
                            </Box>

                            <Box my={1}>
                                <Divider style={{ backgroundColor: "darkgray" }} />
                            </Box>

                            <Box>
                                <Chip label={reporterName} variant="outlined" size="small" />
                                <Chip label={`${dayjs(orderCreatedOn).format("DD.MM.YY")} | ${formatDaysElapsed(orderCreatedOn)}`} variant="outlined" size="small" />
                            </Box>
                        </Box>
                    </Grid>

                    <Grid item>
                        <Box component={Paper} variant="outlined" display="flex" alignItems="center" elevation={0} p={2}>
                            <Box display="flex" flexDirection="column" width="100%">
                                <Box display="flex" justifyContent="space-between" flexWrap="wrap">
                                    <Typography color="textSecondary">{`Description`}</Typography>
                                    <Box style={{ overflow: "auto", maxHeight: "100px", whiteSpace: "pre-wrap" }}>
                                        <Typography>{description}</Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>

                {/* Clock in / clock out */}
                <Grid item>
                    <WorkshopLineClock equipmentId={equipmentId} workshopOrderId={workshopOrderId} userId={user.id} />
                </Grid>
            </Grid>
        </Layout>
    );
}

export default WorkshopOrder;
